import { useState, useEffect, useCallback } from "react";

import { useSelector } from "react-redux";

import { ACTION_COLUMN_NAME } from "constants/aggrid";

import { getSelectedViewIdByTable } from "selectors";

import { useHasChanged } from "./useHasChanged";
import { useIsMobile } from "./useScreenSize";

export const useHandleAgGridColumnDefs = (
  columnDefs,
  agGridInstance,
  tableName,
) => {
  const [handledColumnDefs, setHandledColumnDefs] = useState(columnDefs);

  const selectedViewId =
    useSelector(getSelectedViewIdByTable(tableName)) || null;

  const isMobile = useIsMobile();

  const shouldLockPinned = useCallback(
    colDef => (isMobile ? true : colDef.lockPinned),
    [isMobile],
  );

  const shouldBePinned = useCallback(
    colDef =>
      isMobile && colDef.headerName !== ACTION_COLUMN_NAME
        ? false
        : colDef.pinned,
    [isMobile],
  );

  const shouldBeHidden = useCallback(
    colDef => {
      if (isMobile) {
        if (colDef.showOnMobile) {
          return false;
        }
        if (colDef.hideOnMobile) {
          return true;
        }
        // always want to show the actions column
        return colDef.headerName !== ACTION_COLUMN_NAME ? colDef.hide : false;
      }
      return colDef.hide;
    },
    [isMobile],
  );

  const buildChildren = useCallback(
    colDef => {
      if (isMobile && colDef.children) {
        return colDef.children.map(childColDef => {
          return {
            ...childColDef,
            ...((childColDef.hideOnMobile || colDef.hideOnMobile) && {
              hide: true,
            }),
          };
        });
      }

      return colDef.children;
    },
    [isMobile],
  );

  const setColumnWidth = useCallback(
    colDef => {
      if (isMobile) {
        return colDef.headerName === ACTION_COLUMN_NAME
          ? colDef.mobileWidth || 60
          : colDef.width;
      }
      return colDef.width;
    },
    [isMobile],
  );

  const setColumnFlex = useCallback(
    colDef => (isMobile && colDef.flexOnMobile ? 1 : null),
    [isMobile],
  );

  const columnDefinitionsHaveChanged = useHasChanged(columnDefs);

  useEffect(() => {
    if (agGridInstance) {
      const instanceColumnDefs = agGridInstance.api.getColumnDefs();

      // reset row heights on grid ready - to ensure mobile heights are adhered to
      agGridInstance.api.resetRowHeights();

      // When columnDefs change, use the incoming defs as the reference so that
      // mobile transforms are applied to the new definitions. Otherwise use the
      // grid's current state (which may include saved-view column ordering).
      const referenceColumnDefs =
        columnDefinitionsHaveChanged || !instanceColumnDefs.length
          ? columnDefs
          : instanceColumnDefs;

      // handle custom mobileValues on column defs
      const updatedColumnDefs = Object.values(referenceColumnDefs).map(
        colDef => ({
          ...colDef,
          lockPinned: shouldLockPinned(colDef),
          pinned: shouldBePinned(colDef),
          hide: shouldBeHidden(colDef),
          children: buildChildren(colDef),
          flex: setColumnFlex(colDef),
          width: setColumnWidth(colDef),
        }),
      );
      setHandledColumnDefs(updatedColumnDefs);
      agGridInstance.api.refreshCells();
    }
  }, [
    isMobile,
    agGridInstance,
    columnDefs,
    columnDefinitionsHaveChanged,
    selectedViewId,
    buildChildren,
    setColumnFlex,
    setColumnWidth,
    shouldBeHidden,
    shouldBePinned,
    shouldLockPinned,
  ]);
  return handledColumnDefs;
};
